<template>
  <div class="order-book-table" :class="{ 'compact-mode': isCompactMode }">
    <!-- Tab Navigation & Filter -->
    <div class="table-controls">
      <div class="tab-group">
        <button
          v-for="tab in options"
          :key="tab"
          class="tab-btn"
          :class="{ active: currentOption === tab }"
          @click="emitOption(tab)"
        >
          <svg v-if="tab === 'Buy'" class="tab-icon" viewBox="0 0 20 20" fill="none">
            <path d="M10 4V16M10 4L6 8M10 4L14 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else class="tab-icon" viewBox="0 0 20 20" fill="none">
            <path d="M10 16V4M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="tab-text">{{ tab }}</span>
          <span v-if="isCompactMode" class="tab-count">{{ tab === 'Buy' ? buyCount : sellCount }}</span>
        </button>
      </div>
      <div
        class="filter-wrapper"
        v-if="!isPreview"
      >
        <button class="filter-btn" @click="showFilter = !showFilter">
          <svg class="filter-icon" viewBox="0 0 20 20" fill="none">
            <path d="M3 5H17M6 10H14M8 15H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Filter
          <span v-if="filterOption" class="filter-badge">1</span>
        </button>
        <FilterComponent
          v-if="showFilter"
          title="Filter by Status"
          :options="filterOptions"
          @close="showFilter = false"
          @filter="filter"
          class="filter-dropdown"
        />
      </div>
    </div>

    <!-- Orders List -->
    <div class="orders-container">
      <!-- Loading State -->
      <div v-if="table === 'orderTable'" class="loading-state">
        <SmallLoader />
      </div>

      <!-- Empty State -->
      <div v-else-if="tableRows.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="1.5"/>
            <path d="M16 20H32M16 28H28" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </div>
        <p class="empty-text">No {{ currentOption.toLowerCase() }} orders found</p>
        <p class="empty-subtext">Your {{ currentOption.toLowerCase() }} orders will appear here</p>
      </div>

      <!-- Orders List -->
      <div v-else class="orders-list">
        <div
          v-for="(order, index) in tableRows"
          :key="index"
          class="order-card clickable"
          @click="showOrderDetails(index)"
        >
          <div class="order-main">
            <div class="order-asset" v-html="order.name"></div>
            <div class="order-details">
              <div class="detail-item">
                <span class="detail-label">Shares</span>
                <span class="detail-value">{{ order.amount }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Price</span>
                <span class="detail-value price">{{ order.price }}</span>
              </div>
              <div class="detail-item" v-if="!isCompactMode">
                <span class="detail-label">Date</span>
                <span class="detail-value">{{ order.date }}</span>
              </div>
            </div>
          </div>
          <div class="order-status-wrapper">
            <span
              class="status-badge"
              :class="{
                'status-completed': order.status === 'Completed',
                'status-pending': order.status === 'Pending',
                'status-cancelled': order.status === 'Cancelled'
              }"
            >
              <span class="status-dot"></span>
              {{ order.status }}
            </span>
            <button
              v-if="order.status === 'Pending' && !isCompactMode"
              class="cancel-btn"
              @click="cancel(order.id)"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      class="pagination-wrapper"
      v-if="totalPage > 0 && !isPreview"
    >
      <div class="pagination-info">
        Showing page {{ page }} of {{ totalPage }}
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="page === 1"
          @click="gotoPage(page - 1)"
        >
          <svg viewBox="0 0 20 20" fill="none">
            <path d="M12 15L7 10L12 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="page-numbers">
          <button
            v-for="pageNum in visiblePages"
            :key="pageNum"
            class="page-num"
            :class="{ active: page === pageNum }"
            @click="gotoPage(pageNum)"
          >
            {{ pageNum }}
          </button>
        </div>
        <button
          class="page-btn"
          :disabled="page === totalPage"
          @click="gotoPage(page + 1)"
        >
          <svg viewBox="0 0 20 20" fill="none">
            <path d="M8 5L13 10L8 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <SmallModal v-if="showCancelModal" @close="showCancelModal = false">
      <template v-slot:content>
        <div class="cancel-modal">
          <div class="modal-icon warning">
            <svg viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2"/>
              <path d="M24 16V28M24 32V33" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 class="modal-title">Cancel Order</h3>
          <p class="modal-text">
            Are you sure you want to cancel this order? This action cannot be undone.
          </p>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="showCancelModal = false">
              Keep Order
            </button>
            <button class="modal-btn danger" @click="cancel()">
              Cancel Order
            </button>
          </div>
        </div>
      </template>
    </SmallModal>

    <!-- Cancel Success Modal -->
    <SmallModal v-if="showCancelSuccess" @close="showCancelSuccess = false">
      <template v-slot:content>
        <div class="cancel-modal">
          <div class="modal-icon success">
            <svg viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2"/>
              <path d="M16 24L22 30L32 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="modal-title">Order Cancelled</h3>
          <p class="modal-text">
            Your order has been successfully cancelled.
          </p>
          <div class="modal-actions single">
            <button class="modal-btn primary" @click="showCancelSuccess = false">
              Done
            </button>
          </div>
        </div>
      </template>
    </SmallModal>

    <!-- Order Details Modal -->
    <MediumModal v-if="showOrderDetailsModal" @close="closeOrderDetailsModal">
      <template v-slot:content>
        <div class="order-details-modal" v-if="selectedOrder">
          <div class="order-details-header">
            <div class="order-token-info">
              <img 
                :src="selectedOrder.token?.image" 
                :alt="selectedOrder.token?.name"
                class="order-token-image"
              />
              <div class="order-token-text">
                <h3 class="order-token-symbol">{{ selectedOrder.token?.symbol }}</h3>
                <p class="order-token-name">{{ selectedOrder.token?.name }}</p>
              </div>
            </div>
            <span 
              class="order-type-badge"
              :class="{ 'buy': selectedOrder.type === 'Buy', 'sell': selectedOrder.type === 'Sell' }"
            >
              {{ selectedOrder.type }} Order
            </span>
          </div>

          <div class="order-details-content">
            <div class="order-detail-row">
              <span class="order-detail-label">Order ID</span>
              <span class="order-detail-value mono">{{ selectedOrder._id }}</span>
            </div>
            <div class="order-detail-row">
              <span class="order-detail-label">Status</span>
              <span 
                class="order-detail-status"
                :class="{
                  'status-completed': selectedOrderStatus === 'Completed',
                  'status-pending': selectedOrderStatus === 'Pending',
                  'status-cancelled': selectedOrderStatus === 'Cancelled'
                }"
              >
                {{ selectedOrderStatus }}
              </span>
            </div>
            <div class="order-detail-row">
              <span class="order-detail-label">Shares</span>
              <span class="order-detail-value">{{ selectedOrder.amount?.toFixed(2) }}</span>
            </div>
            <div class="order-detail-row">
              <span class="order-detail-label">Price per Share</span>
              <span class="order-detail-value">{{ formatAmountToDollar(selectedOrder.price, 2, selectedOrder.token?.currency || 'USD') }}</span>
            </div>
            <div class="order-detail-row">
              <span class="order-detail-label">Total Value</span>
              <span class="order-detail-value highlight">{{ formatAmountToDollar(selectedOrder.amount * selectedOrder.price, 2, selectedOrder.token?.currency || 'USD') }}</span>
            </div>
            <div class="order-detail-row">
              <span class="order-detail-label">Date Created</span>
              <span class="order-detail-value">{{ formatISODate(selectedOrder.createdAt) }}</span>
            </div>
          </div>

          <div class="order-details-actions">
            <button 
              v-if="selectedOrderStatus === 'Pending'" 
              class="cancel-order-btn"
              @click="cancelFromDetailsModal"
            >
              <svg class="btn-icon" viewBox="0 0 20 20" fill="none">
                <path d="M6 6L14 14M6 14L14 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              Cancel Order
            </button>
            <button class="close-details-btn" @click="closeOrderDetailsModal">
              Close
            </button>
          </div>
        </div>
      </template>
    </MediumModal>
  </div>
</template>

<script>
import FilterComponent from "@/components/general/FilterComp.vue";
import SmallModal from "@/components/general/SmallModal.vue";
import MediumModal from "@/components/general/MediumModal.vue";
import SmallLoader from "@/components/notification/SmallLoader.vue";
import { formatAmountToDollar, formatISODate } from "@/utils/helpers.js";
import { mapActions, mapState } from "vuex";

export default {
  name: "OrderBookTable",

  components: {
    FilterComponent,
    SmallModal,
    MediumModal,
    SmallLoader,
  },

  props: {
    currentEsop: { type: Object, default: () => {} },
    /**
     * Render the short, chrome-free version for a dashboard card.
     *
     * investor-app inferred this from its overview route name. That does not survive a port:
     * this app routes the Treasury overview as "Invest", and it HAS a different page called
     * "OverviewDetails" (the company dashboard) — so the inference silently flipped and the
     * card rendered full-width with filters and pagination. The caller states it now.
     */
    compact: { type: Boolean, default: false },
  },

  data() {
    return {
      showFilter: false,
      showCancelModal: false,
      showCancelSuccess: false,
      showOrderDetailsModal: false,
      selectedOrder: null,
      orderId: "",
      options: ["Buy", "Sell"],
      option: "",
      filterOptions: ["Completed", "Pending", "Failed"],
      filterOption: "",
      currentOption: "Buy",
      availableStatuses: ["Pending", "Completed", "Cancelled"],
      buyOrderCount: 0,
      sellOrderCount: 0,
      tableHeaders: [
        "",
        "Shares",
        "Amount",
        "Price",
        "Type",
        "Date",
        "Status",
        "",
      ],
      tableHeadersOption: [
        "",
        "Shares",
        "Amount",
        "Price",
        "Date",
        "Status",
        "",
      ],
      tableRows: [],
      table: "",
    };
  },

  computed: {
    // Preview placement: five rows, no filter bar, no pagination.
    isPreview() {
      return this.compact || this.$route.name === "EsopOverview";
    },

    ...mapState({
      smallLoading: (state) => state.smallLoading,
      orderBook: (state) => state.TokenModule.orderBook,
      page: (state) => state.TokenModule.orderBookPage,
      pages: (state) => state.TokenModule.orderBookPages,
    }),
    isCompactMode() {
      return this.isPreview;
    },
    buyCount() {
      return this.buyOrderCount;
    },
    sellCount() {
      return this.sellOrderCount;
    },
    totalPage() {
      return this.pages || 0;
    },
    visiblePages() {
      const pages = [];
      const max = Math.min(5, this.totalPage);
      const half = Math.floor(max / 2);
      let start = Math.max(1, this.page - half);
      let end = Math.min(this.totalPage, start + max - 1);
      
      if (end - start + 1 < max) {
        start = Math.max(1, end - max + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    selectedOrderStatus() {
      if (!this.selectedOrder) return '';
      if (this.selectedOrder.status === 'Active') return 'Pending';
      if (this.selectedOrder.status === 'Fulfilled') return 'Completed';
      if (this.selectedOrder.status === 'Terminated') return 'Cancelled';
      return this.selectedOrder.status;
    },
  },

  created() {
    this.emitOption("Buy");
    this.fetchOrderCounts();
  },

  methods: {
    ...mapActions("TokenModule", ["getOrderBooks", "cancelOrder", "getOrderBookCount"]),

    formatAmountToDollar,
    formatISODate,

    async fetchOrderCounts() {
      // Only fetch counts in compact mode (Overview page)
      if (!this.isCompactMode) return;
      
      let tokenId;
      this.$route.name === "EsopOverview"
        ? (tokenId = this.currentEsop?.token._id)
        : null;
      
      const basePayload = {
        page: 1,
        limit: 100, // Fetch enough to get accurate count
        status: "Active",
        tokenId,
      };
      
      // Fetch both counts in parallel
      const [buyResult, sellResult] = await Promise.all([
        this.getOrderBookCount({ ...basePayload, type: "Buy" }),
        this.getOrderBookCount({ ...basePayload, type: "Sell" }),
      ]);
      
      if (buyResult?.count !== undefined) {
        this.buyOrderCount = buyResult.count;
      }
      if (sellResult?.count !== undefined) {
        this.sellOrderCount = sellResult.count;
      }
    },

    showOrderDetails(index) {
      this.selectedOrder = this.orderBook[index];
      this.showOrderDetailsModal = true;
    },

    closeOrderDetailsModal() {
      this.showOrderDetailsModal = false;
      this.selectedOrder = null;
    },

    cancelFromDetailsModal() {
      this.orderId = this.selectedOrder._id;
      this.showOrderDetailsModal = false;
      this.showCancelModal = true;
    },

    filter(status) {
      if (!status) return;
      this.filterOption = status;
      const payload = {
        page: 1,
        type: this.currentOption,
        limit: 10,
        status: this.filterOption,
      };
      this.tableRows = [];
      this.table = "orderTable";
      this.getOrderBooks(payload);
    },

    emitOption(option) {
      this.table = "orderTable";
      this.tableRows = [];
      let tokenId;
      let type;
      let limit;

      switch (option) {
        case "Buy":
          type = "Buy";
          this.currentOption = "Buy";
          this.option = "Buy";
          break;
        case "Sell":
          type = "Sell";
          this.currentOption = "Sell";
          this.option = "Sell";
          break;
      }
      this.isPreview ? (limit = 5) : (limit = 10);
      this.$route.name === "EsopOverview"
        ? (tokenId = this.currentEsop?.token._id)
        : null;
      const payload = {
        page: 1,
        type,
        limit: limit,
        status:
          this.isPreview ||
          this.$route.name === "EsopOverview"
            ? "Active"
            : "",
        tokenId,
      };
      if (
        !this.isPreview &&
        this.$route.name !== "EsopOverview"
      ) {
        delete payload.status;
      }
      this.getOrderBooks(payload);
    },

    gotoPage(page) {
      let limit;
      let tokenId;
      this.tableRows = [];
      this.table = "orderTable";
      this.isPreview ? (limit = 5) : (limit = 10);
      this.$route.name === "EsopOverview"
        ? (tokenId = this.currentEsop?.token._id)
        : null;
      const payload = {
        page: page,
        type: this.currentOption,
        limit: limit,
        status:
          this.$route.name === "EsopOverview" ? "Active" : this.filterOption,
        tokenId,
      };
      if (this.filterOption === "" && this.$route.name !== "EsopOverview") {
        delete payload.status;
      }
      this.getOrderBooks(payload);
    },

    updateTableRow(orders) {
      this.table = "";
      let status;
      this.tableRows = orders.map((order) => {
        if (order.status === "Active") status = "Pending";
        if (order.status === "Fulfilled") status = "Completed";
        if (order.status === "Terminated") status = "Cancelled";

        return {
          id: `<span class="tw-hidden">${order._id}</span>`,
          name: `<span class="tw-flex tw-flex-wrap tw-items-start">
                    <img
                      src=${order.token.image}
                      class="tw-mr-8 tw-w-8"
                      alt="blue cone"
                      loading="lazy"
                    />
                    <div>
                      <span class='tw-block'>
                        ${order.token.symbol}
                      </span>
                      <span class='tw-text-gray-lightest2'>
                      ${order.token.name}
                      </span>
                    </div>
                    </span>`,
          amount: order?.amount.toFixed(2),
          price: formatAmountToDollar(order?.price, 2, order.token?.currency || 'USD'),
          date: `${formatISODate(order.createdAt)}`,
          status: status,
        };
      });
    },

    async cancel(data) {
      if (data) this.orderId = data.split(">")[1].split("<")[0];
      if (!this.showCancelModal) return (this.showCancelModal = true);
      let tokenId;
      this.tableRows = [];
      this.table = "orderTable";
      this.showCancelModal = false;
      const response = await this.cancelOrder(this.orderId);
      if (response) {
        this.showCancelSuccess = true;
        let limit;
        this.isPreview ? (limit = 5) : (limit = 10);
        this.$route.name === "EsopOverview"
          ? (tokenId = this.currentEsop?.token._id)
          : null;
        const payload = {
          page: this.page,
          type: this.currentOption,
          limit: limit,
          status:
            this.isPreview ||
            this.$route.name === "EsopOverview"
              ? "Active"
              : "",
          tokenId,
        };
        if (
          !this.isPreview &&
          this.$route.name !== "EsopOverview"
        ) {
          delete payload.status;
        }
        this.getOrderBooks(payload);
      }
    },
  },

  watch: {
    orderBook(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateTableRow(this.orderBook);
        // Update the count for current option based on actual orders loaded
        if (this.currentOption === 'Buy') {
          this.buyOrderCount = newValue.length;
        } else if (this.currentOption === 'Sell') {
          this.sellOrderCount = newValue.length;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$bg-primary: #F8FAFC;
$bg-card: #FFFFFF;
$bg-card-hover: #F1F5F9;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$border-color: #E2E8F0;
$accent: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$success-color: #10B981;
$success-bg: #ECFDF5;
$warning-color: #F59E0B;
$warning-bg: #FFFBEB;
$error-color: #EF4444;
$error-bg: #FEF2F2;

.order-book-table {
  @include fadeIn;

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPACT MODE (Overview & ESOP pages)
  // ═══════════════════════════════════════════════════════════════════════════
  &.compact-mode {
    .table-controls {
      margin-bottom: 12px;
    }

    .tab-group {
      width: 100%;
      background: $bg-primary;
      padding: 4px;
      border-radius: 8px;
      gap: 4px;
    }

    .tab-btn {
      flex: 1;
      justify-content: center;
      padding: 8px 12px;
      font-size: 12px;
      background: transparent;
      border: none;
      border-radius: 6px;

      &:hover {
        background: rgba($bg-card, 0.5);
      }

      &.active {
        background: $bg-card;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        color: $text-primary;

        .tab-icon {
          color: $accent;
        }
      }

      .tab-icon {
        width: 14px;
        height: 14px;
      }
    }

    .tab-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      font-size: 10px;
      font-weight: 500;
      background: rgba($accent, 0.1);
      color: $accent;
      border-radius: 9px;
      margin-left: 4px;
    }

    .orders-container {
      min-height: 120px;
    }

    .loading-state {
      min-height: 120px;
    }

    .empty-state {
      padding: 32px 16px;

      .empty-icon {
        width: 48px;
        height: 48px;
        margin-bottom: 12px;
      }

      .empty-text {
        font-size: 14px;
      }

      .empty-subtext {
        font-size: 12px;
      }
    }

    .orders-list {
      gap: 8px;
    }

    .order-card {
      flex-direction: column;
      align-items: flex-start;
      padding: 12px;
      gap: 10px;
      border-radius: 8px;
    }

    .order-main {
      width: 100%;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    }

    .order-asset {
      min-width: auto;

      :deep(img) {
        width: 28px;
        height: 28px;
        margin-right: 10px;
      }

      :deep(span) {
        span:first-child {
          font-size: 13px;
        }

        span:last-child {
          font-size: 11px;
        }
      }
    }

    .order-details {
      gap: 16px;
    }

    .detail-item {
      .detail-label {
        font-size: 10px;
      }

      .detail-value {
        font-size: 13px;
      }
    }

    .order-status-wrapper {
      width: 100%;
      padding-top: 10px;
      border-top: 1px solid $border-color;
    }

    .status-badge {
      padding: 4px 10px;
      font-size: 11px;
    }
  }
}

// Table Controls
.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.tab-group {
  display: flex;
  gap: 8px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $bg-card-hover;
    color: $text-primary;
  }

  &.active {
    background: $btn-primary;
    color: white;
    border-color: $btn-primary;

    .tab-icon {
      color: white;
    }

    .tab-count {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }

  .tab-icon {
    width: 16px;
    height: 16px;
    color: $text-muted;
  }

  .tab-text {
    &::after {
      content: ' Orders';
    }
  }

  .tab-count {
    display: none;
  }
}

.filter-wrapper {
  position: relative;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $bg-card-hover;
    border-color: $text-muted;
  }

  .filter-icon {
    width: 16px;
    height: 16px;
  }

  .filter-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 400;
    color: white;
    background: $accent;
    border-radius: 9px;
  }
}

.filter-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 50;
}

// Orders Container
.orders-container {
  min-height: 200px;
}

// Loading State
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;

  .empty-icon {
    width: 64px;
    height: 64px;
    color: $text-muted;
    margin-bottom: 16px;
  }

  .empty-text {
    font-size: 15px;
    font-weight: 400;
    color: $text-primary;
    margin: 0 0 4px;
  }

  .empty-subtext {
    font-size: 13px;
    font-weight: 400;
    color: $text-muted;
    margin: 0;
  }
}

// Orders List
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: $bg-card-hover;
    border-color: darken($border-color, 5%);
  }
}

.order-main {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

.order-asset {
  display: flex;
  align-items: center;
  min-width: 150px;

  :deep(img) {
    width: 32px;
    height: 32px;
    border-radius: 7px;
    margin-right: 12px;
  }

  :deep(span) {
    display: flex;
    flex-direction: column;

    span:first-child {
      font-size: 14px;
      font-weight: 400;
      color: $text-primary;
    }

    span:last-child {
      font-size: 12px;
      font-weight: 400;
      color: $text-muted;
    }
  }
}

.order-details {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    gap: 16px;
  }
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .detail-label {
    font-size: 11px;
    font-weight: 400;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .detail-value {
    font-size: 14px;
    font-weight: 400;
    color: $text-primary;

    &.price {
      color: $success-color;
    }
  }
}

.order-status-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 7px;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.status-completed {
    color: $success-color;
    background: $success-bg;

    .status-dot {
      background: $success-color;
    }
  }

  &.status-pending {
    color: $warning-color;
    background: $warning-bg;

    .status-dot {
      background: $warning-color;
    }
  }

  &.status-cancelled {
    color: $error-color;
    background: $error-bg;

    .status-dot {
      background: $error-color;
    }
  }
}

.cancel-btn {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 400;
  color: $error-color;
  background: transparent;
  border: 1px solid $error-color;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $error-color;
    color: white;
  }
}

// Pagination
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid $border-color;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 16px;
  }
}

.pagination-info {
  font-size: 13px;
  font-weight: 400;
  color: $text-muted;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: $text-secondary;
  }

  &:hover:not(:disabled) {
    background: $bg-card-hover;
    border-color: $text-muted;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-num {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 400;
  color: $text-secondary;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $bg-card-hover;
  }

  &.active {
    color: white;
    background: $btn-primary;
    border-color: $btn-primary;
  }
}

// Modal Styles
.cancel-modal {
  text-align: center;
  padding: 16px 0;

  .modal-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;

    &.warning {
      color: $warning-color;
    }

    &.success {
      color: $success-color;
    }
  }

  .modal-title {
    font-size: 18px;
    font-weight: 400;
    color: $text-primary;
    margin: 0 0 8px;
  }

  .modal-text {
    font-size: 14px;
    font-weight: 400;
    color: $text-secondary;
    margin: 0 0 24px;
    line-height: 1.5;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;

    &.single {
      .modal-btn {
        min-width: 120px;
      }
    }
  }

  .modal-btn {
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 400;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.secondary {
      color: $text-secondary;
      background: $bg-card;
      border: 1px solid $border-color;

      &:hover {
        background: $bg-card-hover;
      }
    }

    &.danger {
      color: white;
      background: $error-color;
      border: 1px solid $error-color;

      &:hover {
        background: darken($error-color, 8%);
      }
    }

    &.primary {
      color: white;
      background: $btn-primary;
      border: 1px solid $btn-primary;

      &:hover {
        background: $btn-primary-hover;
      }
    }
  }
}

// Order Details Modal Styles
.order-details-modal {
  padding: 8px;

  .order-details-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid $border-color;
  }

  .order-token-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .order-token-image {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
  }

  .order-token-text {
    .order-token-symbol {
      font-size: 18px;
      font-weight: 600;
      color: $text-primary;
      margin: 0 0 2px;
    }

    .order-token-name {
      font-size: 14px;
      color: $text-secondary;
      margin: 0;
    }
  }

  .order-type-badge {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;

    &.buy {
      background: $success-bg;
      color: $success-color;
    }

    &.sell {
      background: $error-bg;
      color: $error-color;
    }
  }

  .order-details-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }

  .order-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba($border-color, 0.5);

    &:last-child {
      border-bottom: none;
    }
  }

  .order-detail-label {
    font-size: 14px;
    color: $text-secondary;
  }

  .order-detail-value {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;

    &.mono {
      font-family: monospace;
      font-size: 12px;
      color: $text-secondary;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &.highlight {
      font-size: 16px;
      font-weight: 600;
      color: $accent;
    }
  }

  .order-detail-status {
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;

    &.status-completed {
      background: $success-bg;
      color: $success-color;
    }

    &.status-pending {
      background: $warning-bg;
      color: $warning-color;
    }

    &.status-cancelled {
      background: $error-bg;
      color: $error-color;
    }
  }

  .order-details-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .cancel-order-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 500;
    color: white;
    background: $error-color;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    .btn-icon {
      width: 18px;
      height: 18px;
    }

    &:hover {
      background: darken($error-color, 8%);
    }
  }

  .close-details-btn {
    width: 100%;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 500;
    color: $text-secondary;
    background: $bg-primary;
    border: 1px solid $border-color;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $bg-card-hover;
    }
  }
}

// Clickable order card cursor
.order-card.clickable {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}
</style>
